import { useQuery } from "@apollo/client";
import { Tooltip } from "@mui/material";
import { useMemo, useState } from "react";

import { Button } from "@/components/atoms/Button/styles";
import { StyledDataTable } from "@/components/molecules/StyledDataTable";
import {
  CallPlanInfoFragment,
  GetTreatmentHistoryQuery,
  GetTreatmentHistoryQueryVariables,
} from "@/graphql/graphql";
import { PlaylistTableWrapper, RightHeaderDiv, RightStyledH1 } from "@/pages/UserDetails/styles";

import { GET_TREATMENT_HISTORY } from "../UserInfo/components/TreatmentHistoryTable/queries";
import {
  buildCallPlanColumns,
  duplicateProgramWeekEarlierPlanIds,
  getCallPlanDeleteState,
  getCallPlanEditState,
} from "./columns";
import CreateCallPlanModal from "./CreateCallPlanModal";
import {
  CREATE_CALL_PLAN_DISABLED_PROGRAM_ENDED_MESSAGE,
  isActiveProgram,
} from "./CreateCallPlanModal/CreateCallPlanModal.utils";
import ViewCallPlanModal from "./ViewCallPlanModal/ViewCallPlanModal";

type CallPlansProps = {
  accountId: number;
  callPlans: CallPlanInfoFragment[];
};

const CallPlans: React.FC<CallPlansProps> = ({ accountId, callPlans }) => {
  const [createCallPlanModalOpen, setCreateCallPlanModalOpen] = useState(false);
  const [callPlanToEdit, setCallPlanToEdit] = useState<CallPlanInfoFragment | null>(null);
  const [callPlanToView, setCallPlanToView] = useState<CallPlanInfoFragment | undefined>();
  const { data: programsData, loading: programsLoading } = useQuery<
    GetTreatmentHistoryQuery,
    GetTreatmentHistoryQueryVariables
  >(GET_TREATMENT_HISTORY, {
    variables: { accountId },
    fetchPolicy: "network-only",
  });

  const closeCreateCallPlanModal = () => {
    setCreateCallPlanModalOpen(false);
    setCallPlanToEdit(null);
  };

  const closeViewCallPlanModal = () => {
    setCallPlanToView(undefined);
  };

  const openEditFromView = (callPlan: CallPlanInfoFragment) => {
    setCallPlanToView(undefined);
    setCallPlanToEdit(callPlan);
    setCreateCallPlanModalOpen(true);
  };

  const duplicateWeekEarlierPlanIds = useMemo(
    () => duplicateProgramWeekEarlierPlanIds(callPlans),
    [callPlans]
  );

  const activeProgram = useMemo(() => {
    if (!programsLoading && programsData) {
      return programsData.treatmentHistory.find(isActiveProgram);
    }
  }, [programsLoading, programsData]);

  const tableColumns = useMemo(
    () =>
      buildCallPlanColumns(
        (row) => {
          setCallPlanToEdit(row);
          setCreateCallPlanModalOpen(true);
        },
        duplicateWeekEarlierPlanIds,
        !activeProgram
      ),
    [activeProgram, duplicateWeekEarlierPlanIds]
  );

  const viewModalEditState = useMemo(
    () =>
      callPlanToView &&
      getCallPlanEditState(callPlanToView, duplicateWeekEarlierPlanIds, !activeProgram),
    [activeProgram, callPlanToView, duplicateWeekEarlierPlanIds]
  );

  const viewModalDeleteState = useMemo(
    () => callPlanToView && getCallPlanDeleteState(callPlanToView, duplicateWeekEarlierPlanIds),
    [callPlanToView, duplicateWeekEarlierPlanIds]
  );

  return (
    <>
      <RightHeaderDiv>
        <RightStyledH1>
          Call Plans
          <Tooltip title={!activeProgram ? CREATE_CALL_PLAN_DISABLED_PROGRAM_ENDED_MESSAGE : ""}>
            <span>
              <Button
                disabled={!activeProgram}
                onClick={() => {
                  setCallPlanToEdit(null);
                  setCreateCallPlanModalOpen(true);
                }}
              >
                Create New Call Plan
              </Button>
            </span>
          </Tooltip>
        </RightStyledH1>
      </RightHeaderDiv>
      <PlaylistTableWrapper>
        <StyledDataTable
          columns={tableColumns}
          data={callPlans}
          defaultSortFieldId="dateRange"
          defaultSortAsc={false}
          onRowClicked={(row) => setCallPlanToView(row)}
        />
      </PlaylistTableWrapper>
      <ViewCallPlanModal
        callPlanToView={callPlanToView}
        editState={viewModalEditState}
        deleteState={viewModalDeleteState}
        onClose={closeViewCallPlanModal}
        onRequestEdit={openEditFromView}
      />
      <CreateCallPlanModal
        accountId={accountId}
        callPlans={callPlans}
        activeProgram={activeProgram}
        callPlanToEdit={callPlanToEdit}
        open={createCallPlanModalOpen}
        onClose={closeCreateCallPlanModal}
      />
    </>
  );
};

export default CallPlans;
